import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePortalNavigate } from '@/hooks/usePortalPath';
import { userActions } from '@core/user/user.action';
import { useUserStore } from '@core/user/useUserStore';
import { UserStatus } from '@core/generated/gql-types';
import {
  actionAuthSubmitButtonClassName,
  actionFormInputClassName,
} from '@core/theme/actionTheme.util';
import { AuthHeader } from '@/components/auth/AuthHeader';

const PHONE_REGEX = /^(?:\+61|0)4\d{8}$/;

type FormState = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
};

const validateForm = (values: FormState): string | null => {
  const firstName = values.firstName.trim();
  const lastName = values.lastName.trim();
  const phoneNumber = values.phoneNumber.trim();

  if (firstName.length < 2 || firstName.length > 35) {
    return 'First name must be between 2 and 35 characters';
  }

  if (lastName.length < 2 || lastName.length > 35) {
    return 'Last name must be between 2 and 35 characters';
  }

  if (!PHONE_REGEX.test(phoneNumber)) {
    return 'Enter a valid Australian mobile number (e.g. 0412345678)';
  }

  return null;
};

const ClientOnboarding = () => {
  const { user, checkAuth } = useAuth();
  const portalNavigate = usePortalNavigate();
  const { fetchCurrentUser } = useUserStore();
  const [formState, setFormState] = useState<FormState>({
    firstName: user?.firstName?.trim() ?? '',
    lastName: user?.lastName?.trim() ?? '',
    phoneNumber: user?.phoneNumber?.trim() ?? '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const validationError = validateForm(formState);
    if (validationError) {
      setError(validationError);
      setIsLoading(false);
      return;
    }

    if (!user?.id) {
      setError('Unable to save your profile. Please sign in again.');
      setIsLoading(false);
      return;
    }

    try {
      await userActions.updateUser(user.id, {
        firstName: formState.firstName.trim(),
        lastName: formState.lastName.trim(),
        phoneNumber: formState.phoneNumber.trim(),
      });

      await fetchCurrentUser();
      await checkAuth();

      const updatedUser = useUserStore.getState().currentUser;
      if (updatedUser?.status === UserStatus.Active) {
        portalNavigate('/jobs', { replace: true });
        return;
      }

      setIsComplete(true);
    } catch (err) {
      console.error('Error saving onboarding profile:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (isComplete) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-white">
        <div className="w-full max-w-md px-6 py-8 text-center">
          <AuthHeader title="Thanks for registering!" />
          <p className="text-sm text-gray-600">
            Your profile has been saved. You can continue using the portal while
            your account is reviewed.
          </p>
          <button
            type="button"
            onClick={() => portalNavigate('/jobs', { replace: true })}
            className={`${actionAuthSubmitButtonClassName} mt-6`}
          >
            Continue to jobs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-white">
      <div className="w-full max-w-md px-6 py-8">
        <AuthHeader
          title="Tell us about you"
          subtitle="Enter your details to complete your profile"
        />

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-700"
              >
                First name (legal)
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                className={actionFormInputClassName}
                placeholder="Enter your first name"
                value={formState.firstName}
                onChange={(e) =>
                  setFormState({ ...formState, firstName: e.target.value })
                }
              />
            </div>

            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-gray-700"
              >
                Last name (legal)
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                required
                className={actionFormInputClassName}
                placeholder="Enter your last name"
                value={formState.lastName}
                onChange={(e) =>
                  setFormState({ ...formState, lastName: e.target.value })
                }
              />
            </div>

            <div>
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-medium text-gray-700"
              >
                Phone number
              </label>
              <input
                id="phoneNumber"
                name="phoneNumber"
                type="tel"
                required
                className={actionFormInputClassName}
                placeholder="0412345678"
                value={formState.phoneNumber}
                onChange={(e) =>
                  setFormState({ ...formState, phoneNumber: e.target.value })
                }
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`${actionAuthSubmitButtonClassName} disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {isLoading ? 'Saving...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ClientOnboarding;
